import ButtonsListContainer from 'components/Button/ButtonsListContainer';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import React from 'react';
import { IStyle } from 'Styles/styleModel';
import {
  useAssignCrystal,
  useOnCopyCrystals,
  useOnDeleteCrystals,
  useOnHarvestNewCrystal,
  useOnPasteCryo,
  useOnPasteSoaking,
} from './hooks';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { useCrystals, useCurrentTripCheckedCrystals } from 'redux/hooks/crystal';
import { createSynchTrip } from 'components/synchrotron/utils';
import { isCrystalApplicableForCopy, isCrystalLocked } from './utils';
import { useSelectedTripId } from 'redux/hooks/synchrotron';
import { useSelectedDrawingTool, useWellDrawingStatus } from 'redux/hooks/well';
import {
  DrawingType,
  OverlayTools,
} from 'components/well/drawingManager/constants';
import { useRMDispatch } from 'redux/hooks';
import { updateCrystalLock } from 'redux/reducers/wellReducer';
import { useSelectedDropImageParameters, useLastSelectedWellNavigationDetails } from 'redux/hooks/experiment';

const CrystalMenuButtons = () => {
  const crystals = useCrystals();
  const checkedCrystals = useCurrentTripCheckedCrystals();
  const harvestNewCrystal = useOnHarvestNewCrystal();
  const deleteCrystals = useOnDeleteCrystals();
  const assignCrystal = useAssignCrystal();
  const copyCrystal = useOnCopyCrystals();
  const pasteCryoprotectant = useOnPasteCryo();
  const pasteSoaking = useOnPasteSoaking();
  const selectedOverlay = useSelectedDrawingTool();
  const selectedTripId = useSelectedTripId();
  const crystalLocked = isCrystalLocked(crystals, checkedCrystals[0]);
  const canCopyCrystal = isCrystalApplicableForCopy(crystals, checkedCrystals);
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const dispatch = useRMDispatch();
  const {wellNum, plateID} = wellNavigationDetails || {};
  const {dropNumber, regionName} = selectedDropImageParameters || {};
  const drawingStatus = useWellDrawingStatus(plateID, wellNum, dropNumber, regionName);

  const hanldeCreateSynchTrip = async () => {
    const { newSynchTrip } = await createSynchTrip();
    if (newSynchTrip === null) {
      return null;
    }
    window.open(
      `/synchrotrontrip/${newSynchTrip.id}`,
      `/synchrotrontrip/${newSynchTrip.id}`,
    );
  };


  const handleCrystalLock = (locked: boolean) => {
    dispatch(
      updateCrystalLock({
        id: checkedCrystals[0],
        drawingType: DrawingType.CRYSTAL,
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber: dropNumber,
        regionName,
        locked,
      }),
    );
  };

  const toolButtons: RMCustomButtonProps[] = [
    {
      Icon: <RMSvgIcon  id='exp-crsytalmenubuttons-svg-newharvest-icon' name="NewHarvest" height={20} width={20} />,
      titleTip: 'Harvest Crystal',
      isDisabled: selectedTripId > 0 || drawingStatus === undefined,
      onClick: harvestNewCrystal,
      showBorder: true,
      customStyle:
        selectedOverlay === OverlayTools.CRYSTAL
          ? { ...styles.toolsButton, backgroundColor: 'rgb(133, 186, 255)' }
          : styles.toolsButton,
      buttonMainContainerStyle: styles.toolsContent,
      contentContainerClass: 'hover:bg-blue-100',
    },
    {
      Icon: <RMSvgIcon id='exp-crsytalmenubuttons-svg-actions-icon' name={crystalLocked ? 'Lock' : 'Unlock'} height={20} width={20} />,
      titleTip: crystalLocked ? 'Unlock' : 'Lock',
      isDisabled: checkedCrystals.length !== 1,
      onClick: () => {
        handleCrystalLock(crystalLocked ? false : true);
      },
      showBorder: true,
      customStyle:
        checkedCrystals.length === 1 && crystalLocked
          ? { ...styles.toolsButton, backgroundColor: 'rgb(133, 186, 255)' }
          : styles.toolsButton,
      buttonMainContainerStyle: styles.toolsContent,
      contentContainerClass: 'hover:bg-blue-100',
      iconToDisplayOnHover: (
        <RMSvgIcon name={crystalLocked ? 'Unlock' : 'Lock'} height={20} width={20} />
      ),
    },
    {
      Icon: <RMSvgIcon id='exp-crsytalmenubuttons-svg-copy-icon' name="Copy" height={20} width={20} />,
      titleTip: 'Copy',
      isDisabled: !canCopyCrystal,
      onClick: copyCrystal,
      showBorder: true,
      customStyle: styles.toolsButton,
      buttonMainContainerStyle: styles.toolsContent,
      contentContainerClass: 'hover:bg-blue-100',
    },
    {
      Icon: <RMSvgIcon id='exp-crsytalmenubuttons-svg-pastecryo-icon' name="PasteCryoprotectant" height={20} width={20} />,
      titleTip: 'Paste Cryoprotectant',
      isDisabled: !checkedCrystals.length,
      onClick: pasteCryoprotectant,
      showBorder: true,
      customStyle: styles.toolsButton,
      buttonMainContainerStyle: styles.toolsContent,
      contentContainerClass: 'hover:bg-blue-100',
    },
    {
      Icon: <RMSvgIcon id='exp-crsytalmenubuttons-svg-pastesoaking-icon' name="PasteSoaking" height={20} width={20} />,
      titleTip: 'Paste Soaking',
      isDisabled: !checkedCrystals.length,
      onClick: pasteSoaking,
      showBorder: true,
      customStyle: styles.toolsButton,
      buttonMainContainerStyle: styles.toolsContent,
      contentContainerClass: 'hover:bg-blue-100',
    },
    {
      Icon: <RMSvgIcon id='exp-crsytalmenubuttons-svg-del-icon' name="Delete" height={20} width={20} />,
      titleTip: 'Delete Crystal',
      isDisabled: !checkedCrystals.length,
      onClick: () => {
        deleteCrystals(plateID, wellNum, dropNumber, regionName);
      },
      showBorder: true,
      customStyle: checkedCrystals.length
        ? { ...styles.toolsButton, backgroundColor: '#EB3324' }
        : styles.toolsButton,
      buttonMainContainerStyle: styles.toolsContent,
      contentContainerClass: 'hover:bg-blue-100',
    },
    {
      Icon: <RMSvgIcon id='exp-crsytalmenubuttons-svg-assigncrystal-icon' name="AssignCrystal" height={20} width={20} fill="white" />,
      titleTip: 'Assign Crystal',
      isDisabled: !checkedCrystals.length,
      onClick: assignCrystal,
      showBorder: true,
      customStyle: checkedCrystals.length
        ? { ...styles.toolsButton, backgroundColor: 'rgb(0, 106, 245)' }
        : { ...styles.toolsButton, backgroundColor: '#808080' },
      buttonMainContainerStyle: styles.toolsContent,
      contentContainerClass: 'hover:bg-blue-100',
    },
    {
      Icon: <RMSvgIcon id='exp-crsytalmenubuttons-svg-createsynchtrip-icon' name="CreateTrip" height={20} width={20} />,
      titleTip: 'Create Synchrotron Trip',
      isDisabled: false,
      onClick: hanldeCreateSynchTrip,
      showBorder: true,
      customStyle: { ...styles.toolsButton, backgroundColor: '#009F17' },
      buttonMainContainerStyle: styles.toolsContent,
      contentContainerClass: 'hover:bg-blue-100',
    },
  ];

  return (
    <div id='exp-crsytalmenubuttons-container' style={styles.toolsContainer}>
      <ButtonsListContainer
        containerStyleClass="align-items-center"
        buttonsList={toolButtons}
      />
    </div>
  );
};
const styles: IStyle = {
  tools: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  toolsContent: {
    paddingInline: '1%',
    paddingRight: '3%',
  },
  toolsContainer: {
    display: 'flex',
    paddingBlock: '2%',
    paddingInline: '5%',
    alignItems: 'center',
    justifyContent: 'end',
  },
  toolsButton: {
    paddingLeft: '5%',
    outline: '2px solid #CCC',
    border: '1px solid #999 !important',
    borderRadius: '5px',
    paddingTop: '2%',
    margin: '1%',
    paddingBottom: '1%',
    height: '3vh',
    width: '1.5vw',
  },
};
export default CrystalMenuButtons;
